import React, { useCallback, useMemo, useState } from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useGetHealthPredictions, useGetHealthReport } from '~/hooks/apis/cruises/useGetHealthReport';

import type {
  CompetitorGapVsLiveMerch,
  DealPageRecommendation,
  DealStructuringRecommendation,
  DifferentiatedStrength,
  HealthReportQuery,
  InclusionBreakdownRow,
  InclusionPerformanceRow,
  InclusionTrend,
  OfferTileOrderingRecommendation,
  OpportunityAndRiskSignal,
  PredictedBestSeller,
  Predictions,
  TopOfferByBookingsRow,
  TopOfferByMarginRow,
} from '~/services/cruises/CruiseIqService';

type Period = HealthReportQuery['period'];
const MAX_INCLUSION_CHART_ROWS = 10;

function stopAccordionSummaryToggle(event: React.MouseEvent) {
  event.stopPropagation();
}

function buildBarChartData(rows: Array<InclusionBreakdownRow>) {
  const byVendor: Record<string, { vendorCode: string; STANDARD: number; LUX_PLUS: number }> = {};
  for (const row of rows) {
    if (!byVendor[row.vendorCode]) {
      byVendor[row.vendorCode] = { vendorCode: row.vendorCode, STANDARD: 0, LUX_PLUS: 0 };
    }
    if (row.inclusionType === 'STANDARD' || row.inclusionType === 'LUX_PLUS') {
      byVendor[row.vendorCode][row.inclusionType] += row.bookingCount;
    }
  }
  return Object.values(byVendor);
}

type InclusionPerformanceChartRow = InclusionPerformanceRow & {
  chartLabel: string;
  fullLabel: string;
};

function truncateLabel(label: string, maxLength = 18): string {
  if (label.length <= maxLength) {
    return label;
  }

  return `${label.slice(0, maxLength - 1)}…`;
}

function buildInclusionPerformanceChartData(rows: Array<InclusionPerformanceRow>): Array<InclusionPerformanceChartRow> {
  return [...rows]
    .sort(
      (left, right) => right.bookingCount - left.bookingCount || left.inclusionName.localeCompare(right.inclusionName),
    )
    .slice(0, MAX_INCLUSION_CHART_ROWS)
    .map((row) => ({
      ...row,
      fullLabel: row.inclusionName,
      chartLabel: truncateLabel(row.inclusionName),
      totalCommission: row.totalCommission ?? 0,
    }));
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function formatCurrencyAxisTick(value: number): string {
  if (Math.abs(value) >= 1000) {
    return `$${Math.round(value / 1000)}k`;
  }

  return `$${Math.round(value)}`;
}

function formatPercentAxisTick(value: number): string {
  return `${value}%`;
}

function formatInclusionPerformanceTooltipValue(value: number | string, name: string): [string, string] {
  if (typeof value !== 'number') {
    return [String(value), name];
  }

  if (name === 'Total margin') {
    return [`$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, name];
  }

  if (name === 'Avg margin %') {
    return [`${value.toFixed(1)}%`, name];
  }

  return [value.toLocaleString(), name];
}

function TrendIcon({ direction }: { direction: InclusionTrend['direction'] }) {
  if (direction === 'up') return <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (direction === 'down') return <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
}

function TrendChip({ direction, percentChange }: { direction: InclusionTrend['direction']; percentChange: number }) {
  const color = direction === 'up' ? 'success' : direction === 'down' ? 'error' : 'default';
  const sign = percentChange > 0 ? '+' : '';
  return (
    <Chip
      label={`${sign}${percentChange.toFixed(1)}%`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.75rem' }}
    />
  );
}

function renderConfidenceChip(confidence: InclusionTrend['confidence']) {
  const color = confidence === 'High' ? 'success' : confidence === 'Medium' ? 'warning' : 'default';
  return (
    <Chip
      label={`${confidence} confidence`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
}

function renderExpectedImpactChip(expectedImpact: InclusionTrend['expectedImpact']) {
  const color = expectedImpact === 'both' ? 'secondary' : 'primary';
  return (
    <Chip
      label={expectedImpact}
      size="small"
      color={color}
      variant="filled"
      sx={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '0.75rem' }}
    />
  );
}

function renderPredictionMeta(
  confidence: InclusionTrend['confidence'],
  expectedImpact: InclusionTrend['expectedImpact'],
) {
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {renderExpectedImpactChip(expectedImpact)}
      {renderConfidenceChip(confidence)}
    </Stack>
  );
}

function renderPredictionSectionHeading(children: React.ReactNode) {
  return (
    <Typography
      variant="subtitle2"
      color="primary.main"
      fontWeight={700}
      sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {children}
    </Typography>
  );
}

/** Flex row children need minWidth:0 + wrapping so long labels don’t steal width from sibling columns. */
const predictionRowTextWrapSx = {
  minWidth: 0,
  overflowWrap: 'break-word' as const,
  wordBreak: 'break-word' as const,
};

function renderContextChip(label: string, color: 'default' | 'primary' | 'secondary') {
  return <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600 }} />;
}

function OfferRankCard({
  title,
  offers,
  statLabel,
  renderStat,
}: {
  title: string;
  offers: Array<TopOfferByBookingsRow | TopOfferByMarginRow>;
  statLabel: string;
  renderStat: (offer: TopOfferByBookingsRow | TopOfferByMarginRow) => string;
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {offers.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        )}
        {offers.map((offer, idx) => (
          <Stack
            key={offer.offerId}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ py: 1.5, borderBottom: idx < offers.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Typography variant="h5" fontWeight={800} color="primary" sx={{ fontFamily: 'monospace', minWidth: 32 }}>
              #{idx + 1}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {offer.offerDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {offer.vendorName} | {offer.departureDate}
              </Typography>
              {(offer as TopOfferByMarginRow).totalCommission != null && (
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  ${(offer as TopOfferByMarginRow).totalCommission!.toLocaleString()} total margin
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {renderStat(offer)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {statLabel}
              </Typography>
            </Box>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

function renderGapConfidenceChip(confidence: CompetitorGapVsLiveMerch['confidence']) {
  return renderConfidenceChip(confidence);
}

function PredictionsPanel({
  predictions,
  isLoading,
  error,
  title = 'AI Predictions & Recommendations',
  loadingMessage = 'Crawling Cruise Guru, loading performance data, generating predictions…',
  errorPrefix = 'Failed to generate AI predictions',
}: {
  predictions: Predictions | undefined;
  isLoading: boolean;
  error: Error | null;
  title?: string;
  loadingMessage?: string;
  errorPrefix?: string;
}) {
  const [cruiseGuruExpanded, setCruiseGuruExpanded] = useState(false);
  const handleCruiseGuruAccordionChange = useCallback((_event: React.SyntheticEvent, isExpanded: boolean) => {
    setCruiseGuruExpanded(isExpanded);
  }, []);

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: '2px solid', borderColor: 'primary.main', textAlign: 'center' }}
      >
        <CircularProgress size={24} sx={{ mr: 1.5 }} />
        <Typography variant="body2" color="text.secondary" component="span">
          {loadingMessage}
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
        {errorPrefix}: {error.message}
      </Alert>
    );
  }

  if (!predictions) {
    return null;
  }

  const predictionSubsectionCount = [
    predictions.inclusionTrends.length,
    predictions.predictedBestSellers.length,
    predictions.offerTileOrderingRecommendations.length,
    predictions.dealPageRecommendations.length,
    predictions.dealStructuringRecommendations.length,
    predictions.opportunityAndRiskSignals.length,
  ].filter((n) => n > 0).length;

  const predictionCardSx = {
    borderRadius: 3,
    height: '100%',
    ...(predictionSubsectionCount === 1 ? { gridColumn: '1 / -1' as const } : {}),
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>

      <Accordion
        expanded={cruiseGuruExpanded}
        onChange={handleCruiseGuruAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: (theme) =>
            cruiseGuruExpanded ? theme.palette.background.paper : alpha(theme.palette.primary.main, 0.08),
          borderStyle: cruiseGuruExpanded ? 'solid' : 'dashed',
          borderWidth: cruiseGuruExpanded ? 1 : 2,
          borderColor: cruiseGuruExpanded ? 'divider' : 'primary.main',
          transition: (theme) =>
            theme.transitions.create(['border-color', 'border-style', 'border-width', 'background-color'], {
              duration: 200,
            }),
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
                boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 30, color: 'inherit' }} />
            </Box>
          }
          aria-label={cruiseGuruExpanded ? 'Collapse Cruise Guru comparison' : 'Expand Cruise Guru comparison'}
          sx={{
            px: 1.5,
            py: 0,
            minHeight: 88,
            cursor: 'pointer',
            userSelect: 'none',
            alignItems: 'center',
            '&:hover': {
              bgcolor: (theme) =>
                cruiseGuruExpanded ? theme.palette.action.hover : alpha(theme.palette.primary.main, 0.12),
            },
            '&:focus-visible': { bgcolor: 'action.focus' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
              pl: 1,
              mr: 0,
              transition: (theme) => theme.transitions.create('transform', { duration: 200 }),
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              py: 1.5,
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              gap: 1,
            },
          }}
        >
          <Stack spacing={0.75} sx={{ width: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.35 }}>
              Cruise Guru & live deals comparison
            </Typography>
            {!cruiseGuruExpanded && (
              <Typography variant="body2" color="primary.main" sx={{ lineHeight: 1.45 }}>
                Click this panel (or the chevron) to expand &mdash; gaps vs our live merch, strengths, and reference
                URLs.
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip
              label={predictions.cruiseGuru.crawlSucceeded ? 'Cruise Guru listing OK' : 'Crawl issue'}
              color={predictions.cruiseGuru.crawlSucceeded ? 'success' : 'warning'}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" component="span">
              <a
                href={predictions.cruiseGuru.listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopAccordionSummaryToggle}
              >
                Source hub
              </a>
              {predictions.cruiseGuru.crawlSucceeded
                ? ` · ${predictions.cruiseGuru.extractedCampaignCount} campaign links`
                : predictions.cruiseGuru.crawlErrorMessage != null && predictions.cruiseGuru.crawlErrorMessage !== ''
                ? ` — ${predictions.cruiseGuru.crawlErrorMessage}`
                : ''}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0.5, px: 1.5, pb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary.main">
            vs our live deals & inclusions
          </Typography>
          <Typography variant="body2" sx={{ mb: predictions.competitorComparison.gapsVsOurLiveMerch.length ? 2 : 0 }}>
            {predictions.competitorComparison.summaryOneLiner}
          </Typography>

          {predictions.competitorComparison.gapsVsOurLiveMerch.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="error.main" display="block" sx={{ mb: 1 }}>
                Gaps & missing angles
              </Typography>
              {predictions.competitorComparison.gapsVsOurLiveMerch.map((gap: CompetitorGapVsLiveMerch, idx: number) => (
                <Stack key={idx} spacing={0.5} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={700}>
                    {gap.competitorPush}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Our situation:</strong> {gap.ourGap}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Suggestion:</strong> {gap.suggestion}
                  </Typography>
                  {(gap.cruiseGuruCampaignUrlsToMimic ?? []).length > 0 && (
                    <Stack spacing={0.25} sx={{ pt: 0.5 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        Cruise Guru references
                      </Typography>
                      {(gap.cruiseGuruCampaignUrlsToMimic ?? []).map((href) => (
                        <Typography key={href} variant="caption" sx={{ wordBreak: 'break-all' }}>
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {href}
                          </a>
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  <Box>{renderGapConfidenceChip(gap.confidence)}</Box>
                </Stack>
              ))}
            </Box>
          )}

          {predictions.competitorComparison.differentiatedStrengths.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="success.main" display="block" sx={{ mb: 1 }}>
                Where we&apos;re differentiated
              </Typography>
              {predictions.competitorComparison.differentiatedStrengths.map(
                (row: DifferentiatedStrength, idx: number) => (
                  <Stack key={idx} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.75 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {row.point}
                    </Typography>
                    {renderGapConfidenceChip(row.confidence)}
                  </Stack>
                ),
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {predictions.inclusionTrends.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Inclusion Trends')}
              {predictions.inclusionTrends.map((trend: InclusionTrend, idx: number) => (
                <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
                  <TrendIcon direction={trend.direction} />
                  <Box sx={{ flex: 1, ...predictionRowTextWrapSx }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600} sx={predictionRowTextWrapSx}>
                        {trend.inclusionType}
                      </Typography>
                      <TrendChip direction={trend.direction} percentChange={trend.percentChange} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {trend.reasoning}
                    </Typography>
                    {renderPredictionMeta(trend.confidence, trend.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}

        {predictions.predictedBestSellers.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Predicted Best Sellers')}
              {predictions.predictedBestSellers.map((seller: PredictedBestSeller, idx: number) => (
                <Stack
                  key={idx}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="flex-start"
                  spacing={1.5}
                  sx={{ py: 1 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      flex: { sm: '1 1 40%' },
                      maxWidth: { xs: '100%', sm: '48%' },
                      ...predictionRowTextWrapSx,
                    }}
                  >
                    {seller.inclusionType}
                  </Typography>
                  <Box sx={{ flex: { sm: '1 1 55%' }, width: { xs: '100%', sm: 'auto' }, ...predictionRowTextWrapSx }}>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {seller.reasoning}
                    </Typography>
                    {renderPredictionMeta(seller.confidence, seller.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}

        {predictions.offerTileOrderingRecommendations.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Offer Tile Ordering Recommendations')}
              {predictions.offerTileOrderingRecommendations.map((rec: OfferTileOrderingRecommendation, idx: number) => (
                <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
                  <Chip
                    label={idx + 1}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
                  />
                  <Box sx={{ flex: 1, ...predictionRowTextWrapSx }}>
                    <Typography variant="body2" sx={predictionRowTextWrapSx}>
                      <Typography component="span" fontWeight={700}>
                        {rec.action}
                      </Typography>
                      {' — '}
                      <Typography component="span" fontWeight={600}>
                        &quot;{rec.offerName}&quot;
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {rec.reasoning}
                    </Typography>
                    {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}

        {predictions.dealPageRecommendations.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Deal Page Recommendations')}
              {predictions.dealPageRecommendations.map((rec: DealPageRecommendation, idx: number) => (
                <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
                  <Chip
                    label={idx + 1}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
                  />
                  <Box sx={{ flex: 1, ...predictionRowTextWrapSx }}>
                    <Typography variant="body2" sx={predictionRowTextWrapSx}>
                      <Typography component="span" fontWeight={700}>
                        {rec.action}
                      </Typography>
                      {' — '}
                      <Typography component="span" fontWeight={600}>
                        &quot;{rec.dealName}&quot;
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {rec.reasoning}
                    </Typography>
                    {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}

        {predictions.dealStructuringRecommendations.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Deal Structuring Recommendations')}
              {predictions.dealStructuringRecommendations.map((rec: DealStructuringRecommendation, idx: number) => (
                <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
                  <Chip
                    label={idx + 1}
                    size="small"
                    color="info"
                    sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
                  />
                  <Box sx={{ flex: 1, ...predictionRowTextWrapSx }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                      {renderContextChip(rec.segment, 'primary')}
                    </Stack>
                    <Typography variant="body2" fontWeight={600} sx={predictionRowTextWrapSx}>
                      {rec.recommendation}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {rec.reasoning}
                    </Typography>
                    {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}

        {predictions.opportunityAndRiskSignals.length > 0 && (
          <Card variant="outlined" sx={predictionCardSx}>
            <CardContent>
              {renderPredictionSectionHeading('Opportunities & Risks')}
              {predictions.opportunityAndRiskSignals.map((signal: OpportunityAndRiskSignal, idx: number) => (
                <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
                  <Chip
                    label={idx + 1}
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
                  />
                  <Box sx={{ flex: 1, ...predictionRowTextWrapSx }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                      {renderContextChip(signal.type, 'secondary')}
                    </Stack>
                    <Typography variant="body2" fontWeight={600} sx={predictionRowTextWrapSx}>
                      {signal.recommendation}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={predictionRowTextWrapSx}>
                      {signal.reasoning}
                    </Typography>
                    {renderPredictionMeta(signal.confidence, signal.expectedImpact)}
                  </Box>
                </Stack>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>
    </Paper>
  );
}

function HealthReportPanel() {
  const [period, setPeriod] = useState<Period>('30d');
  const { data, isLoading, error } = useGetHealthReport({ period });
  const {
    data: predictions,
    isLoading: predictionsLoading,
    error: predictionsError,
  } = useGetHealthPredictions({ period });
  const barData = useMemo(() => buildBarChartData(data?.inclusionBreakdown ?? []), [data]);
  const inclusionChartData = useMemo(
    () => buildInclusionPerformanceChartData(data?.inclusionPerformance ?? []),
    [data],
  );
  const velocityData = useMemo(
    () =>
      (data?.bookingVelocity ?? []).map((row) => ({
        ...row,
        label: formatDate(row.date),
      })),
    [data],
  );

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading health report...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        {(error as Error).message || 'Failed to load health report'}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value as Period)}>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="60d">Last 60 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <PredictionsPanel
        predictions={predictions}
        isLoading={predictionsLoading}
        error={predictionsError as Error | null}
      />

      <Stack direction="row" spacing={3}>
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Inclusion Sales by Vendor
            </Typography>
            {barData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                No inclusion data for this period
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="vendorCode" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="STANDARD" name="Standard" fill="#1976d2" />
                  <Bar dataKey="LUX_PLUS" name="LuxPlus+" fill="#7b1fa2" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Booking Velocity
            </Typography>
            {velocityData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                No booking data for this period
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="bookingCount" name="Bookings" stroke="#1976d2" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Inclusion Performance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Margin contribution and average margin for each inclusion in the selected period.
              </Typography>
            </Box>
            {inclusionChartData.length > 0 && (
              <Chip
                label={`${inclusionChartData.length} inclusions`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
          {inclusionChartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={6} textAlign="center">
              No inclusion performance data for this period
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={inclusionChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="chartLabel" angle={-18} textAnchor="end" interval={0} height={64} />
                <YAxis yAxisId="left" tickFormatter={formatCurrencyAxisTick} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={formatPercentAxisTick} />
                <Tooltip
                  formatter={formatInclusionPerformanceTooltipValue}
                  labelFormatter={(label) => {
                    const row = inclusionChartData.find((entry) => entry.chartLabel === label);
                    return row?.fullLabel ?? String(label);
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="totalCommission"
                  name="Total margin"
                  fill="#1976d2"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="averageMarginPercent"
                  name="Avg margin %"
                  stroke="#2e7d32"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={3}>
        <Box sx={{ flex: 1 }}>
          <OfferRankCard
            title="Top 5 by Bookings"
            offers={data.topByBookings}
            statLabel="bookings"
            renderStat={(o) => {
              const margin = (o as TopOfferByBookingsRow).averageMarginPercent;
              return margin != null ? `${o.bookingCount} (${margin}%)` : String(o.bookingCount);
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <OfferRankCard
            title="Top 5 by Margin"
            offers={data.topByMargin}
            statLabel="margin"
            renderStat={(o) => `${(o as TopOfferByMarginRow).averageMarginPercent}% (${o.bookingCount})`}
          />
        </Box>
      </Stack>
    </Stack>
  );
}

export default HealthReportPanel;
